import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { rememberConciliation } from '../../lib/caseLinks';
import { M16_ROLES } from '../../lib/roles';
import type { Conciliation, ConciliationOutcome } from '../../lib/types';
import { useCursorList } from '../../lib/useCursorList';
import { EmptyState, ErrorNote, Field, PageHeader, Pagination, Spinner, StatusBadge } from '../../ui/components';

const OUTCOMES: ConciliationOutcome[] = ['PENDING', 'SETTLED', 'FAILED'];

export function ConciliationHome() {
  const { persona } = useAuth();
  const isMediator = persona?.roles.includes(M16_ROLES.MEDIATOR) ?? false;
  const [mine, setMine] = useState(isMediator);
  const [outcome, setOutcome] = useState('');

  const query = new URLSearchParams();
  if (mine) query.set('mediatorId', 'me');
  if (outcome) query.set('outcome', outcome);
  const path = `/api/v1/m16/conciliation${query.toString() ? `?${query.toString()}` : ''}`;
  const listQ = useCursorList<Conciliation>(path);

  // Every conciliation row embeds its grievance — cache the link opportunistically
  // so GrievanceDetail's "View conciliation case" button works after a visit here.
  useEffect(() => {
    for (const c of listQ.items) rememberConciliation(c.grievanceId, c.id);
  }, [listQ.items]);

  return (
    <div>
      <PageHeader title="Conciliation" subtitle="Mediator caseload and officer console for grievances in conciliation." />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        {isMediator ? (
          <label className="flex items-center gap-2 pb-2 text-sm font-medium text-slate-600">
            <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
            My caseload only
          </label>
        ) : null}
        <Field label="Outcome" className="!mb-0">
          <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="">All outcomes</option>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No conciliation cases match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Grievance</th>
                <th className="table-head">Mediator</th>
                <th className="table-head">Started</th>
                <th className="table-head">Deadline</th>
                <th className="table-head">Sessions</th>
                <th className="table-head">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">
                    <Link className="font-semibold text-brand-600 hover:underline" to={`/conciliation/${c.id}`}>
                      {c.grievance?.refNo ?? c.grievanceId}
                    </Link>
                    <div className="mt-0.5 text-[11px] font-sans text-slate-400">{c.grievance?.type}</div>
                  </td>
                  <td className="table-cell font-mono text-xs text-slate-500">{c.mediatorId}</td>
                  <td className="table-cell text-xs">{formatDate(c.startedAt)}</td>
                  <td className="table-cell text-xs">{formatDate(c.deadlineAt)}</td>
                  <td className="table-cell text-xs tabular-nums">
                    {(c.meetings?.length ?? 0)}/{c.maxSessions}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={c.outcome} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination hasMore={listQ.hasMore} onMore={listQ.loadMore} loading={listQ.loadingMore} />
      </div>
    </div>
  );
}
